/**
 * Application shell.
 *
 * RESPONSIVE STRATEGY
 * Two distinct navigation patterns rather than one squeezed into both sizes:
 *
 *   Phone   — a compact top bar plus a fixed bottom tab bar with the five most
 *             used destinations and a "More" sheet for the rest. Bottom
 *             placement is deliberate: it is the only part of a large phone
 *             screen a thumb reaches comfortably.
 *   Desktop — a persistent sidebar, because the horizontal space exists and a
 *             visible nav beats a hidden one.
 *
 * Navigation is filtered by capability, so a user is never shown a destination
 * that would reject them. That is courtesy, not security — the server enforces
 * the same rules again on every request.
 */

import { useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import { NotificationBell } from './NotificationBell';
import { IconNews, NewsUnreadDot } from './NewsNav';
import { OnboardingNavProgress } from './Onboarding';
import { LanguageSwitcher } from './LanguageSwitcher';
import { HelpButton } from './Help';
import { Logo } from './Logo';
import '../lib/contentWatchStrings';
import '../lib/incidentStrings';
import '../lib/craStrings';
import type { Capability } from '@opencomply/shared';
import {
  IconDashboard, IconFramework, IconAssessment, IconGap, IconPlan, IconPolicy,
  IconVendor, IconMore, IconSun, IconMoon, IconLogout, IconCredits,
  IconOrg, IconUsers, IconReport, IconAudit, IconVendorAssessment, IconScan, IconLanguage,
  IconIncident, IconGlobe, IconKey, IconCatalogue, IconWatch, IconChecklist,
  IconShield, IconBook,
} from './icons';

/**
 * Sidebar sections, in the order someone actually works through them.
 *
 * `comply` is the core loop and comes first. `admin` and `account` are at the
 * bottom because they are visited rarely and by few people. Dashboard sits
 * above all of them with no heading: it is the way back, not a category.
 */
type NavGroup = 'comply' | 'controls' | 'thirdParty' | 'operate' | 'share' | 'admin' | 'account';

const NAV_GROUP_ORDER: NavGroup[] = [
  'comply', 'controls', 'thirdParty', 'operate', 'share', 'admin', 'account',
];

interface NavItem {
  to: string;
  labelKey: string;
  /** Shorter label for the phone tab bar, where the full one would truncate. */
  shortLabelKey: string;
  /**
   * Inline fallbacks, used only until a translator ships the key. Every other
   * item's label has lived in the locale files for releases; a newly added one
   * (e.g. Insights) would otherwise render its raw key in the sidebar until the
   * PM merges the locale JSON. Passing these as i18next default values keeps the
   * `t('key', 'default')` contract and is overridden the moment the key lands.
   */
  labelDefault?: string;
  shortLabelDefault?: string;
  icon: (p: { className?: string }) => ReactNode;
  capability: Capability;
  /** Shown in the phone tab bar (max five). */
  primary?: boolean;
  /**
   * Which section of the sidebar this belongs to.
   *
   * Twenty-two flat links made every destination look equally urgent, so a new
   * administrator had no way to tell "answer your assessment" from "manage
   * translations". Grouping by the JOB someone is doing — rather than by which
   * part of the codebase a page lives in — is what lets them skip five sections
   * at a glance.
   */
  group: NavGroup;
}

const NAV: NavItem[] = [
  { to: '/', labelKey: 'nav.dashboard', shortLabelKey: 'nav.shortDashboard', icon: IconDashboard, capability: 'org.read', primary: true, group: 'comply' },
  { to: '/insights', labelKey: 'nav.insights', shortLabelKey: 'nav.shortInsights', labelDefault: 'Insights', shortLabelDefault: 'Insights', icon: IconReport, capability: 'org.read', group: 'comply' },
  { to: '/assessments', labelKey: 'nav.assessments', shortLabelKey: 'nav.shortAssessments', icon: IconAssessment, capability: 'submission.read', primary: true, group: 'comply' },
  { to: '/gaps', labelKey: 'nav.gaps', shortLabelKey: 'nav.shortGaps', icon: IconGap, capability: 'gap.read', primary: true, group: 'comply' },
  { to: '/plans', labelKey: 'nav.plans', shortLabelKey: 'nav.shortPlans', icon: IconPlan, capability: 'plan.readOwn', primary: true, group: 'comply' },
  { to: '/risk-register', labelKey: 'nav.riskRegister', shortLabelKey: 'nav.shortRiskRegister', labelDefault: 'Risk register', shortLabelDefault: 'Risks', icon: IconShield, capability: 'risk.read', group: 'comply' },
  { to: '/incidents', labelKey: 'nav.incidents', shortLabelKey: 'nav.shortIncidents', icon: IconIncident, capability: 'incident.read', group: 'operate' },
  { to: '/calendar', labelKey: 'nav.calendar', shortLabelKey: 'nav.shortCalendar', icon: IconChecklist, capability: 'org.read', group: 'operate' },
  { to: '/frameworks', labelKey: 'nav.frameworks', shortLabelKey: 'nav.shortFrameworks', icon: IconFramework, capability: 'framework.read', group: 'controls' },
  { to: '/catalogue', labelKey: 'nav.catalogue', shortLabelKey: 'nav.shortCatalogue', icon: IconCatalogue, capability: 'framework.read', group: 'controls' },
  { to: '/soa', labelKey: 'nav.soa', shortLabelKey: 'nav.shortSoa', icon: IconChecklist, capability: 'framework.read', group: 'comply' },
  { to: '/cra-conformity', labelKey: 'nav.craConformity', shortLabelKey: 'nav.shortCraConformity', labelDefault: 'CRA conformity', shortLabelDefault: 'CRA', icon: IconShield, capability: 'cra.read', group: 'comply' },
  { to: '/trust-centre', labelKey: 'nav.trustCentre', shortLabelKey: 'nav.shortTrustCentre', icon: IconGlobe, capability: 'trust.manage', group: 'share' },
  { to: '/policies', labelKey: 'nav.policies', shortLabelKey: 'nav.shortPolicies', icon: IconPolicy, capability: 'policy.read', group: 'controls' },
  { to: '/policies/templates', labelKey: 'nav.policyTemplates', shortLabelKey: 'nav.shortPolicyTemplates', icon: IconPolicy, capability: 'template.manage', group: 'controls' },
  { to: '/vendors', labelKey: 'nav.vendors', shortLabelKey: 'nav.shortVendors', icon: IconVendor, capability: 'vendor.read', group: 'thirdParty' },
  { to: '/vendors/assessments', labelKey: 'nav.vendorAssessments', shortLabelKey: 'nav.shortVendorAssessments', icon: IconVendorAssessment, capability: 'vendor.read', group: 'thirdParty' },
  { to: '/scans', labelKey: 'nav.scans', shortLabelKey: 'nav.shortScans', icon: IconScan, capability: 'scan.read', group: 'thirdParty' },
  { to: '/reports', labelKey: 'nav.reports', shortLabelKey: 'nav.shortReports', icon: IconReport, capability: 'report.read', group: 'share' },
  // Administration. Capability-gated, so a CLIENT-tier user who cannot create
  // organisations never sees the entry at all.
  { to: '/organizations', labelKey: 'nav.organizations', shortLabelKey: 'nav.shortOrganizations', icon: IconOrg, capability: 'org.create', group: 'admin' },
  { to: '/users', labelKey: 'nav.users', shortLabelKey: 'nav.shortUsers', icon: IconUsers, capability: 'user.read', group: 'admin' },
  { to: '/translations', labelKey: 'nav.translations', shortLabelKey: 'nav.shortTranslations', icon: IconLanguage, capability: 'org.update', group: 'admin' },
  { to: '/content-watch', labelKey: 'nav.contentWatch', shortLabelKey: 'nav.shortContentWatch', icon: IconWatch, capability: 'framework.manage', group: 'admin' },
  { to: '/audit', labelKey: 'nav.audit', shortLabelKey: 'nav.shortAudit', icon: IconAudit, capability: 'audit.read', group: 'operate' },
  { to: '/credits', labelKey: 'nav.credits', shortLabelKey: 'nav.shortCredits', icon: IconCredits, capability: 'credit.read', group: 'admin' },
  { to: '/getting-started', labelKey: 'nav.gettingStarted', shortLabelKey: 'nav.shortGettingStarted', icon: IconChecklist, capability: 'profile.read', group: 'account' },
  { to: '/manual', labelKey: 'nav.manual', shortLabelKey: 'nav.shortManual', labelDefault: 'Manual', shortLabelDefault: 'Manual', icon: IconBook, capability: 'profile.read', group: 'account' },
  { to: '/news', labelKey: 'nav.news', shortLabelKey: 'nav.shortNews', icon: IconNews, capability: 'profile.read', group: 'account' },
  { to: '/security', labelKey: 'nav.security', shortLabelKey: 'nav.shortSecurity', icon: IconKey, capability: 'profile.update', group: 'account' },
];

/** Theme toggle cycling light → dark → system. */
function useTheme() {
  const [theme, setThemeState] = useState<'light' | 'dark' | 'system'>(() => {
    try {
      return (localStorage.getItem('oc-theme') as 'light' | 'dark') ?? 'system';
    } catch {
      return 'system';
    }
  });

  const setTheme = (next: 'light' | 'dark' | 'system') => {
    setThemeState(next);
    try {
      if (next === 'system') {
        localStorage.removeItem('oc-theme');
        document.documentElement.removeAttribute('data-theme');
      } else {
        localStorage.setItem('oc-theme', next);
        document.documentElement.setAttribute('data-theme', next);
      }
    } catch {
      // Private browsing can block storage; the in-memory choice still applies.
      if (next !== 'system') document.documentElement.setAttribute('data-theme', next);
    }
  };

  return { theme, setTheme };
}

export function Shell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { session, signOut, allows, switchOrganization } = useAuth();
  const { theme, setTheme } = useTheme();
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  const visible = NAV.filter((item) => allows(item.capability));
  const primary = visible.filter((i) => i.primary).slice(0, 4);
  const secondary = visible.filter((i) => !primary.includes(i));

  const cycleTheme = () => setTheme(theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark');

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
      isActive
        ? 'bg-brand/10 text-brand'
        : 'text-ink-secondary hover:bg-surface-sunken hover:text-ink',
    );

  return (
    <div className="min-h-dvh bg-surface-sunken">
      {/* ---------- Desktop sidebar ---------- */}
      <aside
        className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col
                   border-r border-line bg-surface z-30"
      >
        <div className="px-5 py-5 flex items-center gap-2.5">
          <Logo className="w-7 h-7 text-brand" />
          <div className="min-w-0">
            <p className="font-semibold text-ink leading-tight">{t('app.name')}</p>
            <p className="text-[11px] text-ink-muted leading-tight truncate">{t('app.tagline')}</p>
          </div>
        </div>

        <nav className="flex-1 px-3 pb-2 overflow-y-auto" aria-label="Main">
          {/* Dashboard sits above the sections with no heading. It is the way
              back, not a category, and giving it one would imply it belongs to
              the compliance group rather than standing over all of them. */}
          {visible.filter((i) => i.to === '/').map((item) => (
            <NavLink key={item.to} to={item.to} end className={navLinkClass}>
              <item.icon className="w-5 h-5 shrink-0" />
              <span className="truncate">{t(item.labelKey, item.labelDefault ?? item.labelKey)}</span>
            </NavLink>
          ))}

          {NAV_GROUP_ORDER.map((group) => {
            const items = visible.filter((i) => i.group === group && i.to !== '/');
            // A heading over nothing is worse than no heading: it tells a
            // read-only user that a section exists which they cannot reach.
            if (items.length === 0) return null;
            return (
              <section key={group} className="mt-5 first:mt-3">
                <h2 className="px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
                  {t(`nav.group.${group}`)}
                </h2>
                <div className="space-y-1">
                  {items.map((item) => (
                    <NavLink key={item.to} to={item.to} className={navLinkClass}>
                      <item.icon className="w-5 h-5 shrink-0" />
                      <span className="truncate">{t(item.labelKey, item.labelDefault ?? item.labelKey)}</span>
                      {item.to === '/news' && <NewsUnreadDot />}
                    </NavLink>
                  ))}
                </div>
              </section>
            );
          })}
        </nav>

        <OnboardingNavProgress className="mx-3 mb-2" />
        <OrganizationCard onSwitch={switchOrganization} />

        <div className="px-3 pb-4 pt-2 border-t border-line space-y-2">
          <LanguageSwitcher />
          <HelpButton />
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button
              onClick={cycleTheme}
              className="oc-btn-ghost oc-btn-sm flex-1"
              aria-label={`${t('common.theme')}: ${theme}`}
              title={`${t('common.theme')}: ${theme}`}
            >
              {theme === 'dark' ? <IconMoon className="w-4 h-4" /> : <IconSun className="w-4 h-4" />}
              <span className="capitalize">{t(`common.${theme}`)}</span>
            </button>
            <button onClick={() => void signOut()} className="oc-btn-ghost oc-btn-sm" aria-label={t('auth.signOut')}>
              <IconLogout className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* ---------- Mobile top bar ---------- */}
      <header
        className="lg:hidden sticky top-0 z-30 bg-surface/90 backdrop-blur border-b border-line
                   pt-[env(safe-area-inset-top)]"
      >
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2 min-w-0">
            <Logo className="w-6 h-6 text-brand shrink-0" />
            <span className="font-semibold text-ink truncate">
              {session?.organization.name ?? t('app.name')}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <LanguageSwitcher iconOnly />
            <HelpButton iconOnly />
            <button onClick={cycleTheme} className="p-2 rounded-lg text-ink-secondary hover:bg-surface-sunken"
                    aria-label={t('common.theme')}>
              {theme === 'dark' ? <IconMoon className="w-5 h-5" /> : <IconSun className="w-5 h-5" />}
            </button>
            <button onClick={() => void signOut()} className="p-2 rounded-lg text-ink-secondary hover:bg-surface-sunken"
                    aria-label={t('auth.signOut')}>
              <IconLogout className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* ---------- Content ----------
          pb-24 on phones keeps the last row clear of the fixed tab bar. */}
      <main className="lg:pl-64 pb-24 lg:pb-8 pt-4 sm:pt-6">{children}</main>

      {/* ---------- Mobile bottom tab bar ---------- */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-surface/95 backdrop-blur
                   border-t border-line pb-[env(safe-area-inset-bottom)]"
        aria-label="Main"
      >
        <div className="grid grid-cols-5">
          {primary.map((item) => {
            const active = item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={cn(
                  // 56px tall: comfortably above the 44px minimum touch target.
                  'flex flex-col items-center justify-center gap-0.5 h-14 text-[11px] font-medium transition-colors',
                  active ? 'text-brand' : 'text-ink-muted',
                )}
              >
                <item.icon className="w-5 h-5" />
                <span className="max-w-full px-1">{t(item.shortLabelKey, item.shortLabelDefault ?? item.shortLabelKey)}</span>
              </NavLink>
            );
          })}
          <button
            onClick={() => setMoreOpen(true)}
            className="flex flex-col items-center justify-center gap-0.5 h-14 text-[11px] font-medium text-ink-muted"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
          >
            <span className="relative">
              <IconMore className="w-5 h-5" />
              {secondary.some((i) => i.to === '/news') && <NewsUnreadDot variant="corner" />}
            </span>
            <span>{t('nav.more')}</span>
          </button>
        </div>
      </nav>

      {/* ---------- "More" sheet ---------- */}
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex items-end" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMoreOpen(false)} aria-hidden />
          <div className="relative w-full bg-surface-raised rounded-t-2xl border-t border-line
                          pb-[env(safe-area-inset-bottom)] animate-fade-in">
            <div className="flex justify-center pt-3" aria-hidden>
              <div className="w-10 h-1 rounded-full bg-ink/15" />
            </div>
            <nav className="p-3 space-y-1">
              {secondary.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMoreOpen(false)}
                  className={navLinkClass}
                >
                  <item.icon className="w-5 h-5" />
                  {t(item.labelKey, item.labelDefault ?? item.labelKey)}
                  {item.to === '/news' && <NewsUnreadDot />}
                </NavLink>
              ))}
            </nav>
            <div className="px-3 pb-4">
              <OnboardingNavProgress className="mb-2" />
              <OrganizationCard onSwitch={switchOrganization} compact />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The acting-organisation card.
 *
 * Switching organisations re-issues the session token server-side. It is not a
 * client-side filter — the new token is scoped to the new organisation, so the
 * previous tenant's data becomes unreachable rather than merely hidden.
 */
function OrganizationCard({ onSwitch, compact }: {
  onSwitch: (id: string) => Promise<void>;
  compact?: boolean;
}) {
  const { session } = useAuth();
  const { t } = useTranslation();
  if (!session) return null;

  const multiple = session.organizations.length > 1;

  return (
    <div className={cn('px-3', compact ? 'pt-2' : 'py-3 border-t border-line')}>
      <div className="rounded-xl bg-surface-sunken p-3">
        <p className="text-[11px] uppercase tracking-wide text-ink-muted mb-1">
          {t('common.organization')}
        </p>
        {multiple ? (
          <select
            className="oc-input !min-h-[2.25rem] !text-sm mb-2"
            value={session.organization.id}
            onChange={(e) => void onSwitch(e.target.value)}
            aria-label={t('common.organization')}
          >
            {session.organizations.map((org) => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        ) : (
          <p className="text-sm font-medium text-ink truncate mb-1">{session.organization.name}</p>
        )}
        <div className="flex items-center gap-2 flex-wrap text-[11px] text-ink-muted">
          <span>{t(`role.${session.organization.role}`)}</span>
          <span aria-hidden>·</span>
          <span>{t(`tier.${session.organization.tier}`)}</span>
        </div>
      </div>
      <CreditsBadge />
    </div>
  );
}

/** Credit balance, shown only to users who may see it. */
function CreditsBadge() {
  const { t } = useTranslation();
  const { allows, session } = useAuth();

  // Previously this fired an import() and setState DURING RENDER, which is a
  // side effect in the render pass (StrictMode runs it twice) and — worse —
  // cached the first value forever. After spending credits the sidebar kept
  // showing the old balance until a full reload, so the number next to every
  // "this will cost N credits" dialog was quietly wrong.
  //
  // As a query it participates in the cache that the credits and scan screens
  // already invalidate with `invalidateQueries(['credits'])`, so a charge
  // updates it immediately. The org id is in the key so a balance cached for
  // one organisation is never shown after switching to another.
  const orgId = session?.organization.id;
  const canRead = allows('credit.read');
  const { data } = useQuery({
    queryKey: ['credits', orgId, 'balance'],
    queryFn: () => api.get<{ balance: number }>('/credits/balance'),
    enabled: canRead && !!orgId,
    staleTime: 30_000,
  });

  if (!canRead) return null;
  const balance = data?.balance;
  // A failed or in-flight lookup hides the badge rather than showing a zero —
  // "0 credits" is a statement of fact the app would be making up.
  if (balance === undefined) return null;

  return (
    <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-brand/[0.07] text-brand">
      <IconCredits className="w-4 h-4" />
      <span className="text-sm font-medium tabular-nums">{balance}</span>
      <span className="text-xs opacity-80">{t('common.credits')}</span>
    </div>
  );
}
